import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import { CalendarIcon, Timer, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { TeamProject } from "@/types/TeamProject";
import { CollaborativeProject } from "@/types/CollaborativeProject";

interface TeamTimeEntryFormProps {
  onSubmit: (entry: TeamTimeEntryData) => void;
  project: TeamProject | CollaborativeProject;
}

export interface TeamTimeEntryData {
  project_id: string;
  description: string;
  minutes: number;
  date: string;
  is_billable: boolean;
  task_category?: string;
}

export function TeamTimeEntryForm({ onSubmit, project }: TeamTimeEntryFormProps) {
  const [date, setDate] = useState<Date>(new Date());
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [description, setDescription] = useState("");
  const [isBillable, setIsBillable] = useState(true);
  const [taskCategory, setTaskCategory] = useState("");
  const [error, setError] = useState<string>();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);

    const totalMinutes = (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    
    if (totalMinutes === 0) {
      setError("Please enter time spent");
      return;
    }

    if (!description.trim()) {
      setError("Please enter a description");
      return;
    }

    const entry: TeamTimeEntryData = {
      project_id: project.id,
      description: description.trim(),
      minutes: totalMinutes,
      date: date.toISOString(),
      is_billable: isBillable,
      task_category: taskCategory.trim() || undefined,
    };

    onSubmit(entry);
    setHours("");
    setMinutes("");
    setDescription("");
    setTaskCategory("");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium mb-1">Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(date) => date && setDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium mb-1">Time Spent</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="number"
                min="0"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="pl-9"
                placeholder="Hours"
              />
              <Timer className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
            <div className="relative flex-1">
              <Input
                type="number"
                min="0"
                max="59"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder="Minutes"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Task Category (Optional)</label>
        <Input
          value={taskCategory}
          onChange={(e) => setTaskCategory(e.target.value)}
          placeholder="e.g., Development, Design, Meeting"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <div className="relative">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[100px] pl-9"
            placeholder="What did you work on?"
            required
          />
          <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="billable"
          checked={isBillable}
          onCheckedChange={setIsBillable}
        />
        <label htmlFor="billable" className="text-sm font-medium">
          Billable time
        </label>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <Button type="submit" className="w-full">
        Log Time Entry
      </Button>
    </form>
  );
}